/**
 * Detect Seller-Assisted (STAFF_OPERATED) checkout sessions on the customer phone path.
 * Plan §7 / G14 — gateway handoff review only; never reuse Self-Scan helpers.
 */
import {
  CHECKOUT_FLOW_ORIGIN_SELLER_ASSISTED_KIOSK,
  isSellerAssistedKioskOrigin,
  type CheckoutFlowOrigin,
  type CheckoutSessionDisplayLine,
} from 'pi-kiosk-shared';

/** Minimal session shape for SA origin detection (avoids circular checkoutApi imports in tests). */
export interface SellerAssistedOriginSessionFields {
  readonly checkoutFlowOrigin?: CheckoutFlowOrigin | string | null;
  readonly sellerAssistedBasketPublicId?: string | null;
  /** Backend mint typo tolerance until W2/W3 stamp is healed. */
  readonly sellerAssistedpublicId?: string | null;
  readonly sellerAssistedBound?: boolean | null;
  readonly basketVersion?: number | null;
  readonly displayLines?: readonly CheckoutSessionDisplayLine[] | null;
  readonly status?: string | null;
}

export function resolveSellerAssistedBasketPublicId(
  session: SellerAssistedOriginSessionFields | null | undefined,
): string | null {
  if (session == null) {
    return null;
  }
  const primary = session.sellerAssistedBasketPublicId?.trim() ?? '';
  if (primary.length > 0) {
    return primary;
  }
  const typo = session.sellerAssistedpublicId?.trim() ?? '';
  return typo.length > 0 ? typo : null;
}

/**
 * True when CCS is Seller-Assisted origin (metadata and/or basket public id).
 * Prefer JWT-authenticated payment start rebind only for these sessions (FR-06).
 */
export function isSellerAssistedCheckoutOrigin(
  session: SellerAssistedOriginSessionFields | null | undefined,
): boolean {
  if (session == null) {
    return false;
  }
  if (session.sellerAssistedBound === true) {
    return true;
  }
  const origin = session.checkoutFlowOrigin;
  if (
    typeof origin === 'string' &&
    (isSellerAssistedKioskOrigin(origin) ||
      origin === CHECKOUT_FLOW_ORIGIN_SELLER_ASSISTED_KIOSK)
  ) {
    return true;
  }
  return resolveSellerAssistedBasketPublicId(session) !== null;
}
